import { parseISO } from 'date-fns';

import type { Locale } from '~/auth/userContext';

// CAT-5 (full-load safety) testing applies to traction elevators. This covers
// the legacy `CarType` taxonomy (only `Traction`) and the generated
// `ElevatorTypeEnum` variants (mrl/geared/gearless-traction), so both the
// compliance views and the migrated PropertyCars grid classify them the same.
const CAT5_APPLICABLE_ELEVATOR_TYPES = new Set<string>([
  'Traction',
  'mrl-traction',
  'geared-traction',
  'gearless-traction',
]);

/**
 * Reduce a stored-calendar-date value to its date-only form
 * ("2026-02-10T00:00:00Z" -> "2026-02-10"), so it renders through the same
 * date-only code path a `DateField` value already does.
 *
 * Only for a `DateTimeField` whose time component is a parsing artifact, not
 * real information — currently just `Violation.date_open`. It comes from a
 * date-only `<input type="date">`; Django (`TIME_ZONE = "UTC"`, `USE_TZ =
 * True`) parses the bare date as naive midnight and makes it UTC-aware, so
 * the API always serializes it as `<date>T00:00:00Z` with no genuine time of
 * day in it.
 *
 * Do NOT reach for this on a `DateTimeField` with a real time component —
 * `created_at` (row-insert instant, `auto_now_add`) and `resolved_dt` (set to
 * `timezone.now()` in `Violation.resolve()`) are true instants. Those already
 * render correctly through `formatLocaleDate*` below, unstripped: parsing the
 * full instant and localizing it is exactly what a real timestamp needs.
 * Stripping their time would shift the rendered calendar day for any viewer
 * not on UTC — e.g. a violation resolved at 02:00 UTC on 11 February is
 * 10 February, 8pm for a viewer in `America/Chicago`; stripping to "2026-02-11"
 * would render the wrong day for them. Apply this only at the call site that
 * actually has a stored-calendar-date value, before it reaches this file's
 * formatters — not inside them. (AMP-699)
 */
export const toStoredCalendarDate = (value: string) => value.split('T')[0];

/**
 * Render a date for a compliance surface in the viewer's locale.
 *
 * These grids and panels used to hardcode `'en-US'`, so a UK user saw MM/DD/YYYY
 * under a UK label — "06/08/2026" reads as 6 August to them and 8 June to us,
 * with no way to tell which was meant. Pass the active locale so `en-GB` gets
 * DD/MM/YYYY. (CX-416)
 *
 * Parsed with `parseISO`, not `new Date(...)`: the API sends date-only strings
 * ("2026-08-06"), which `new Date` reads as UTC midnight and then renders in
 * local time — one day early for every negative-offset viewer. `parseISO` treats
 * a date-only string as local midnight, so the date shown is the date sent.
 * A genuine instant (a real `DateTimeField` like `created_at`/`resolved_dt`)
 * is expected here too: `parseISO` on a full ISO string with a time and `Z`
 * suffix returns the real instant, and `toLocaleDateString` correctly
 * localizes it to the viewer's calendar day. See {@link toStoredCalendarDate}
 * for the one case (`date_open`) that needs pre-reduction before it reaches
 * here (AMP-699).
 */
const formatLocaleDate = (
  value: string | null | undefined,
  locale: Locale = 'en-US',
  nullValue = '-',
) => {
  return value ? parseISO(value).toLocaleDateString(locale) : nullValue;
};

/**
 * As {@link formatLocaleDate}, but in the zero-padded numeric form the
 * compliance side panel has always used: "07/23/2026" for en-US, "23/07/2026"
 * for en-GB.
 *
 * The panel rendered these through a fixed `format(…, 'MM/dd/yyyy')` pattern, so
 * a UK reader saw a Category B correction deadline of 3 October as "10/03/2026"
 * and took it for 10 March — a month early on a chase deadline, with nothing on
 * screen to say which was meant. The explicit 2-digit fields (rather than
 * `formatLocaleDate`'s locale-default short form) keep the US rendering
 * byte-identical to the pattern it replaces, so only the UK output changes.
 * (CX-416)
 *
 * A genuine instant is expected here too — see `formatLocaleDate` above
 * (AMP-699).
 */
export const formatLocaleDateNumeric = (
  value: string | null | undefined,
  locale: Locale = 'en-US',
  nullValue = '-',
) => {
  return value
    ? parseISO(value).toLocaleDateString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    : nullValue;
};

export const formatLastCat1Test = ({
  value,
  nullValue = '-',
  locale = 'en-US',
}: {
  value: string | null;
  nullValue: string;
  locale?: Locale;
}) => {
  return formatLocaleDate(value, locale, nullValue);
};

export const formatLastCat5Test = ({
  value,
  nullValue = '-',
  elevatorType,
  locale = 'en-US',
}: {
  value: string | null;
  nullValue: string;
  elevatorType: string | null;
  locale?: Locale;
}) => {
  if (!elevatorType || !CAT5_APPLICABLE_ELEVATOR_TYPES.has(elevatorType)) {
    return 'Does not apply';
  }
  return formatLocaleDate(value, locale, nullValue);
};
